// apps/console/src/services/api/domains/robot-tracker.api.ts
//
// GPS-tracker registration (Teltonika ATC700, device kind `tracker`) and the time-windowed position
// history every field device has. Split out of robots.api.ts only to keep that file inside the
// 300-line rule; `robotsApi` spreads these methods in and robots.api.ts re-exports the types, so
// callers import everything from one place.

import { api } from '../client'

/** The tracker profile a registration carries. The IMEI is how the broker recognises the device. */
export interface TrackerProfileInput {
  /** 15 ASCII digits — what the device sends first on every TCP session. */
  imei: string
  model?: string
  iccid?: string
  /** How often the device is configured to send, in seconds (10..86400). Drives roster presence. */
  sendPeriodS?: number
}

/** `POST /robots` for a tracker. `tracker` is required by the backend when `kind === 'tracker'`. */
export interface CreateTrackerInput {
  name: string
  serverId: string
  kind: 'tracker'
  model?: string
  tracker: TrackerProfileInput
}

/** What `POST /robots` returns. `token` is shown once; a tracker never needs it typed anywhere. */
export interface CreatedRobot {
  id: string
  name: string
  kind: string
  serverId: string
  liveLayerId: string
  trailsLayerId: string
  alertsLayerId: string
  token: string
}

/** One stored fix, oldest first. */
export interface RobotTrackPoint {
  lng: number
  lat: number
  recordedAt: string
  heading: number | null
  battery: number | null
}

/** `GET /robots/{robotId}/track` — bounded to 7 days and 5000 points server-side. */
export interface RobotTrack {
  robotId: string
  from: string
  to: string
  count: number
  /** True when the window held more fixes than `limit`; the NEWEST `limit` are returned, older ones dropped. */
  truncated: boolean
  points: RobotTrackPoint[]
  /** Null when fewer than two points — a single fix is not a line. */
  line: { type: 'LineString'; coordinates: Array<[number, number]> } | null
}

export interface RobotTrackQuery {
  from?: string
  to?: string
  limit?: number
}

/**
 * `GET /robots/{robotId}/telemetry/latest` — the newest STORED reading, so the status tile is filled
 * on page load instead of waiting out a send period. `telemetry` is null for a tracker that has
 * never reported. Deliberately without the ICCID and the raw IO map the live frame carries.
 */
export interface LatestTrackerTelemetry {
  robotId: string
  receivedAt: string | null
  telemetry: Record<string, unknown> | null
}

export const robotTrackerApi = {
  /** Register a GPS tracker. Cognito write-role + valid license, like every device registration. */
  create: (input: CreateTrackerInput) => api.post<CreatedRobot>('/robots', input),

  /** One device's stored positions in a window (defaults server-side to the last 24 h). */
  getTrack: (robotId: string, query: RobotTrackQuery = {}) => {
    const params = new URLSearchParams()

    if (query.from) params.set('from', query.from)
    if (query.to) params.set('to', query.to)
    if (typeof query.limit === 'number') params.set('limit', String(query.limit))

    const qs = params.toString()

    return api.get<RobotTrack>(`/robots/${encodeURIComponent(robotId)}/track${qs ? `?${qs}` : ''}`)
  },

  /** The newest stored reading for the tracker tile's first paint. Read-only, tenant-scoped. */
  getLatestTelemetry: (robotId: string) =>
    api.get<LatestTrackerTelemetry>(`/robots/${encodeURIComponent(robotId)}/telemetry/latest`),
}
